import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { failureOf } from '@/api/errors';
import { useErasure, useErasures, useExecuteErasure, useRefuseErasure, useRequestErasure } from '@/api/hooks';
import type { WireErasure } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { FactList, RecordHeader, Table, TokenPanel, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { PageHeader } from '@/app/frame';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { viewerOf } from '@/directory/explainKit';
import { explainErasure } from './explain';
import { localIsoDate } from '@/today/dates';

/**
 * A data subject's request to be forgotten (ADR-0015, ADR-0030; 0058).
 *
 * Recorded by whoever received it, on the day it arrived — the month the
 * subject is owed runs from then. A superadmin carries it out under a
 * `data.erase` token bound to that one request, or declines it with the
 * reason the subject is owed. What the erasure did is counted, and a thread
 * the subject shares with someone else is named as a person's to redact,
 * never claimed as done.
 */
export function erasureState(r: WireErasure): { label: string; tone: 'accent' | 'danger' | 'neutral' | 'warning' } {
  if (r.state === 'executed') return { label: 'ERASED', tone: 'neutral' };
  if (r.state === 'refused') return { label: 'DECLINED', tone: 'neutral' };
  return r.dueOn < localIsoDate(new Date()) ? { label: 'OVERDUE', tone: 'danger' } : { label: 'WAITING', tone: 'warning' };
}

export function Erasures() {
  const navigate = useNavigate();
  const q = useErasures();
  const me = useMeOrThrow();
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const waiting = q.data.requests.filter((r) => r.state === 'requested').length;
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-5 p-6 phone:p-4">
      <header className="flex flex-col gap-1">
        <PageHeader kicker="The data subject" title="Erasure requests" />
        <p className="m-0 text-dense text-meta" data-testid="erasure-count">
          {waiting} waiting · {q.data.viewer.isSuperadmin ? 'every request' : 'the ones you recorded'}
        </p>
      </header>
      <Table<WireErasure>
        title="erasures"
        blurb="Somebody outside the company asked us to forget them. A request is recorded from their contact page on the day it arrived; a superadmin carries it out or declines it, with a month from that day to do so."
        rows={q.data.requests}
        onOpen={(r) => void navigate(`/erasure/${r.id}`)}
        explain={(r) => explainErasure(r, who, viewerOf(me))}
        testId="erasures"
        columns={[
          { label: 'Subject', get: (r) => r.contactName, primary: true },
          { label: 'Received', get: (r) => r.receivedOn },
          { label: 'Due', get: (r) => r.dueOn },
          { label: 'Recorded by', get: (r) => r.requestedBy },
          { label: 'Status', get: (r) => erasureState(r).label, chip: (r) => erasureState(r) },
        ]}
        facets={[{ label: 'Status', get: (r) => erasureState(r).label }]}
      />
    </div>
  );
}

export function ErasurePage() {
  const { id } = useParams();
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const q = useErasure(id ?? null);
  const [flow, setFlow] = useState<'execute' | 'refuse' | null>(null);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={8} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const r = q.data.request;
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Erasure request"
        title={r.contactName}
        subtitle={`Received ${r.receivedOn} · due ${r.dueOn}`}
        chips={[erasureState(r)]}
        actions={
          me.isSuperadmin && r.state === 'requested' ? (
            <>
              <Button variant="danger" onClick={() => setFlow('execute')}>Erase them</Button>
              <Button icon="close" onClick={() => setFlow('refuse')}>Decline</Button>
            </>
          ) : undefined
        }
      />
      <Explainer startOpen label="About this request" context={explainErasure(r, who, viewerOf(me))} />
      <FactList
        facts={[
          ['Recorded by', `${r.requestedBy}, ${when(r.requestedAt)}`],
          ['Decided by', r.decidedBy ? `${r.decidedBy}, ${when(r.decidedAt!)}` : null],
          ['Why declined', r.refusalReason],
        ]}
      />
      {r.outcome && (
        <section className="flex flex-col gap-2" data-testid="erasure-outcome">
          <Kicker>What was erased</Kicker>
          <p className="m-0 text-dense text-secondary">
            {r.outcome.threadsErased} thread{r.outcome.threadsErased === 1 ? '' : 's'} only they were on, unreadable now — the key is destroyed.{' '}
            {r.outcome.addressesMasked} message address{r.outcome.addressesMasked === 1 ? '' : 'es'} and {r.outcome.bookingsMasked} booking
            {r.outcome.bookingsMasked === 1 ? '' : 's'} masked; their name, address and phone are gone from the contact.
            {(r.outcome.manualCommunicationsErased ?? 0) > 0 ? ` ${r.outcome.manualCommunicationsErased} linked manual communication record${r.outcome.manualCommunicationsErased === 1 ? '' : 's'} made unreadable.` : ''}
            {r.outcome.suppressed ? ' Their address is on the suppression list, as a hash, so nothing writes to them again.' : ''}
          </p>
          {r.outcome.threadsShared > 0 && (
            <p className="m-0 text-dense text-danger" data-testid="erasure-shared">
              {r.outcome.threadsShared} thread{r.outcome.threadsShared === 1 ? '' : 's'} they share with someone else still hold what they wrote.
              Destroying those keys would erase the others too; redacting them is a person’s work.
            </p>
          )}
        </section>
      )}
      <Button icon="back" variant="quiet" onClick={() => void navigate('/erasure')}>Back to erasure requests</Button>
      {flow === 'execute' && <ExecuteErasure request={r} onClose={() => setFlow(null)} />}
      {flow === 'refuse' && <RefuseErasure request={r} onClose={() => setFlow(null)} />}
    </div>
  );
}

function ExecuteErasure({ request, onClose }: { request: WireErasure; onClose: () => void }) {
  const run = useExecuteErasure();
  const toast = useToast();
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Erasure · critical"
      title={`Erase ${request.contactName}?`}
      danger
      note="Not reversible"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and erase',
        disabled: token === '' || run.isPending,
        onClick: () =>
          run.mutate(
            { requestId: request.id, supervisionToken: token },
            {
              onSuccess: (r) => {
                toast({ text: `Erased. ${r.outcome.threadsErased} thread(s) destroyed, ${r.outcome.threadsShared} shared thread(s) left for a person.`, tone: 'danger' });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        Every thread only they were on becomes unreadable, their address is masked on every message and booking, and their name leaves the contact. Deals and employments keep pointing at the contact, which now names nobody.
      </p>
      <TokenPanel operation="data.erase" target={`erasure:${request.id}`} token={token} onToken={setToken} canMint />
      {run.isError && <Refusal failure={failureOf(run.error)} compact />}
    </Dialog>
  );
}

function RefuseErasure({ request, onClose }: { request: WireErasure; onClose: () => void }) {
  const refuse = useRefuseErasure();
  const toast = useToast();
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Erasure"
      title="Decline this request?"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Decline',
        disabled: reason.trim().length < 8 || refuse.isPending,
        onClick: () =>
          refuse.mutate(
            { requestId: request.id, reason: reason.trim() },
            { onSuccess: () => { toast({ text: 'Declined. The reason is on the request.' }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">Nothing is erased. The subject is owed the reason — a legal hold, a contract still running.</p>
      <Field label="Why" hint="Written to the request, where whoever answers the subject reads it.">
        <TextInput value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" placeholder="A sentence the subject can act on" />
      </Field>
      {refuse.isError && <Refusal failure={failureOf(refuse.error)} compact />}
    </Dialog>
  );
}

/** From a contact's page: they asked to be forgotten, on this day. */
export function RequestErasure({ contactId, name, onClose }: { contactId: string; name: string; onClose: () => void }) {
  const record = useRequestErasure();
  const toast = useToast();
  const navigate = useNavigate();
  const [receivedOn, setReceivedOn] = useState(localIsoDate(new Date()));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="The data subject"
      title={`${name} asked to be forgotten`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Record the request',
        disabled: record.isPending || receivedOn === '',
        onClick: () =>
          record.mutate(
            { contactId, receivedOn },
            {
              onSuccess: (r) => {
                toast({ text: `Recorded. Due ${r.request.dueOn}; a superadmin decides it.` });
                onClose();
                void navigate(`/erasure/${r.request.id}`);
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        This records the request and puts their address on the suppression list now — they asked us to stop. Nothing is erased yet: a superadmin carries it out,
        or declines it with a reason, within a month of the day they asked.
      </p>
      <Field label="When they asked" hint="The day it arrived, not today, if they differ.">
        <TextInput type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} aria-label="When they asked" />
      </Field>
      {record.isError && <Refusal failure={failureOf(record.error)} compact />}
    </Dialog>
  );
}
